import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData, platformAdvantages, contactCta, faqData } from '@/mocks/about';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 51吃瓜网',
    url: 'https://51chigua.com/about',
    description:
      '51吃瓜网——全网吃瓜爆料一站式平台，成立于2020年，覆盖全网12,800+网红、3,600+明星的动态追踪。',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51chigua.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://51chigua.com/about' },
      ],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Navbar />
      <main>
        {/* 面包屑 */}
        <section className="w-full px-4 md:px-6 pt-24 pb-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: '关于我们', href: '/about' }]} />
          </div>
        </section>

        {/* 平台优势 */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <p className="text-xs font-medium text-accent-600 uppercase tracking-widest mb-2">{aboutData.subtitle}</p>
            <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight mb-10 md:mb-14">
              {aboutData.title}
              <br />
              <span className="text-primary-500">{aboutData.titleHighlight}</span>
            </h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {platformAdvantages.map((adv, i) => (
                <div key={i} className="p-6 rounded-2xl bg-background-50 border border-background-200/40">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 mb-4">
                    <i className={`${adv.icon} text-xl`} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">{adv.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 品牌故事 */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="flex-1">
              <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight mb-6">
                品牌故事与平台实力
              </h2>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed">{aboutData.description}</p>
              <button className="mt-6 px-6 py-3 text-sm font-semibold text-foreground-700 border-2 border-foreground-300/40 rounded-full hover:border-primary-400 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer">
                了解更多
                <i className="ri-arrow-right-line ml-2" />
              </button>
              <div className="mt-8 grid grid-cols-2 gap-6">
                {aboutData.stats.map((stat) => (
                  <div key={stat.label}>
                    <p className="text-3xl md:text-4xl font-black text-primary-500">{stat.value}</p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-[480px]">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src={aboutData.brandImage}
                  alt="51吃瓜网品牌故事"
                  title="51吃瓜网品牌故事"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1 text-xs font-medium text-accent-700 bg-background-50/90 backdrop-blur-sm rounded-full whitespace-nowrap">
                    51吃瓜编辑部
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <p className="text-sm text-background-50 font-semibold">专业团队 · 实时追踪</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 合作联系 */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
              {contactCta.title}
            </h2>
            <p className="mt-4 text-base md:text-lg text-foreground-600 max-w-xl mx-auto">{contactCta.subtitle}</p>
            <button className="mt-8 px-8 py-4 text-sm font-semibold text-background-50 bg-secondary-600 rounded-full hover:bg-secondary-700 transition-all whitespace-nowrap cursor-pointer shadow-lg shadow-secondary-600/20">
              {contactCta.buttonLabel}
              <i className="ri-arrow-right-line ml-2" />
            </button>
            <div className="mt-10 flex items-center justify-center gap-4 md:gap-6 flex-wrap">
              {contactCta.partners.map((url, i) => (
                <div key={i} className="w-32 h-10 rounded-lg bg-background-50 overflow-hidden flex-shrink-0">
                  <img src={url} alt={`合作伙伴 ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqData.map((faq, i) => (
                <div key={i} className="rounded-xl border border-background-200/60 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-background-100/50 transition-colors cursor-pointer"
                  >
                    <span className="font-semibold text-foreground-950 text-sm md:text-base pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-lg text-foreground-400 flex-shrink-0 transition-transform duration-200 ${
                        openFaq === i ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">{faq.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}